import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { plannerAPI } from "../../../api/client";
import PlannerLeftPanel from "../../../components/planner/PlannerLeftPanel";
import PlannerHeader from "../../../components/planner/PlannerHeader";
import PlannerMonthView from "../../../components/planner/PlannerMonthView";
import PlannerWeekView from "../../../components/planner/PlannerWeekView";
import PlannerDayView from "../../../components/planner/PlannerDayView";
import TeamDayView from "../../../components/planner/TeamDayView";
import PlannerSkeleton from "../../../components/planner/PlannerSkeleton";
import { parseLocalDate, expandDayRanges } from "../../../utils/dateUtils";
import "../../../components/planner/Planner.css";
import { PERMISSIONS } from "../../../constants/permissions";

export default function PlannerPage() {
  const { user, permissions } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin =
    !!user?.isAdmin ||
    user?.permissions.includes(PERMISSIONS.SCHEDULE_CONFIGURE_ORG);

  const [selectedUserId, setSelectedUserId] = useState(
    isAdmin ? null : user?.id,
  );
  const [view, setView] = useState("month");
  const [focusDate, setFocusDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [teamData, setTeamData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isTeamView = isAdmin && selectedUserId === null;

  useEffect(() => {
    if (!isAdmin && user?.id) {
      setSelectedUserId(user.id);
    }
  }, [isAdmin, user?.id]);

  useEffect(() => {
    const preselect = searchParams.get("userId");
    if (preselect && isAdmin) {
      setSelectedUserId(preselect);
    }
  }, [searchParams, isAdmin]);

  const fetchData = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      if (isTeamView) {
        const res = await plannerAPI.getTeam({
          year: currentMonth.year,
          month: currentMonth.month,
        });
        const data = res.data;
        if (data?.users) {
          data.users = data.users.map((u) => ({
            ...u,
            days: expandDayRanges(u.dayRanges),
          }));
        }
        setTeamData(data);
        setUserData(null);
      } else {
        const res = await plannerAPI.getUser(selectedUserId, {
          year: currentMonth.year,
          month: currentMonth.month,
        });
        const data = res.data;
        if (data) data.days = expandDayRanges(data.dayRanges);
        setUserData(data);
        setTeamData(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedUserId, currentMonth, isTeamView]);

  const handleDayDoubleClick = (dateStr) => {
    const parsed = parseLocalDate(dateStr) || new Date();
    setFocusDate(parsed);
    if (view === "month") setView("week");
    else if (view === "week") setView("day");
  };

  const handleBack = () => {
    if (view === "day") setView("week");
    else if (view === "week") setView("month");
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      let m = prev.month + direction;
      let y = prev.year;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      if (m < 1) {
        m = 12;
        y -= 1;
      }
      return { year: y, month: m };
    });
    setView("month");
  };

  const leftPanelUsers = useMemo(() => {
    if (teamData?.users) return teamData.users;
    if (userData?.user) return [userData.user];
    return [];
  }, [teamData, userData]);

  return (
    <div className="planner-page">
      <PlannerLeftPanel
        isAdmin={isAdmin}
        selectedUserId={selectedUserId}
        onSelectUser={setSelectedUserId}
        currentUser={user}
        users={leftPanelUsers}
        selectedUserData={isTeamView ? null : userData}
        currentMonth={currentMonth}
        onRefresh={fetchData}
      />

      <div className="planner-right">
        <PlannerHeader
          view={view}
          focusDate={focusDate}
          currentMonth={currentMonth}
          onBack={handleBack}
          onNavigate={navigateMonth}
          selectedUser={selectedUserId && userData ? userData.user : null}
          isTeamView={isTeamView}
        />

        {isLoading ? (
          <PlannerSkeleton />
        ) : view === "month" ? (
          <PlannerMonthView
            currentMonth={currentMonth}
            data={isTeamView ? teamData : userData}
            isTeamView={isTeamView}
            isAdmin={isAdmin}
            onDayDoubleClick={handleDayDoubleClick}
            onRefresh={fetchData}
          />
        ) : view === "week" ? (
          <PlannerWeekView
            focusDate={focusDate}
            data={isTeamView ? teamData : userData}
            isTeamView={isTeamView}
            isAdmin={isAdmin}
            onDayDoubleClick={handleDayDoubleClick}
            onRefresh={fetchData}
          />
        ) : view === "day" && isTeamView ? (
          <TeamDayView
            focusDate={focusDate}
            teamData={teamData}
            isAdmin={isAdmin}
            onRefresh={fetchData}
          />
        ) : (
          <PlannerDayView
            focusDate={focusDate}
            data={userData}
            isAdmin={isAdmin}
            onRefresh={fetchData}
          />
        )}
      </div>
    </div>
  );
}
